import { useState } from 'react';
import { LINKS } from '@/config/contracts';

interface HistoryEntry {
  epoch: number;
  token: 'shield' | 'kat' | 'caps' | 'cifer';
  source: string;
  amount: number;
  tx_hash: string | null;
  created_at: number;
}

interface Props {
  entries: HistoryEntry[];
}

const TOKEN_CONFIG = {
  shield: { dot: '#A855F7', label: 'Shield', amountClass: 'text-shield', suffix: 'pts' },
  kat: { dot: '#EC4899', label: '$KAT', amountClass: 'text-kat', suffix: 'KAT' },
  caps: { dot: '#F59E0B', label: '$CAPS', amountClass: 'text-caps', suffix: 'CAPS' },
  cifer: { dot: '#6366F1', label: 'CIFER', amountClass: 'text-indigo-400', suffix: 'pts' },
};

const SOURCE_ICONS: Record<string, { bg: string; color: string }> = {
  'Daily distribution': { bg: 'bg-success/10', color: 'text-success' },
  'Daily airdrop': { bg: 'bg-success/10', color: 'text-success' },
  'Merkle claim': { bg: 'bg-success/10', color: 'text-success' },
  'Social bonus': { bg: 'bg-social-light', color: 'text-social' },
  'Staking yield': { bg: 'bg-usdt-light', color: 'text-usdt' },
  'Hold duration bonus': { bg: 'bg-accent-light', color: 'text-accent' },
  'CIFER redeemed': { bg: 'bg-indigo-500/10', color: 'text-indigo-400' },
};

type Filter = 'all' | 'caps' | 'kat' | 'shield' | 'cifer';

const PAGE_SIZE = 10;

export function RewardsHistory({ entries }: Props) {
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(0);

  const filtered = filter === 'all' ? entries : entries.filter((e) => e.token === filter);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset to page 0 when filter changes
  const handleFilter = (f: Filter) => {
    setFilter(f);
    setPage(0);
  };

  return (
    <section>
      <h2 className="section-label">Rewards History</h2>
      <div className="glass-card overflow-hidden">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-8 py-4 sm:py-5 border-b border-border-subtle gap-3">
          <div className="flex items-center gap-3">
            <span className="font-display text-base sm:text-lg font-semibold">Activity</span>
            <span className="text-xs font-semibold text-muted bg-glass-bg border border-border-subtle px-2.5 py-0.5 rounded-ternoa">
              {filtered.length} entries
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(['all', 'caps', 'kat', 'shield', 'cifer'] as Filter[]).map((f) => (
              <button
                key={f}
                onClick={() => handleFilter(f)}
                className={`font-display text-xs font-semibold uppercase tracking-widest px-3.5 py-1.5 rounded-ternoa border cursor-pointer transition-all bg-transparent font-body ${
                  filter === f
                    ? 'bg-accent-light border-hairline text-accent'
                    : 'border-border-subtle text-muted hover:text-gray-400 hover:border-border-medium'
                }`}
              >
                {f === 'all' ? 'All' : f === 'caps' ? '$CAPS' : f === 'kat' ? '$KAT' : f === 'cifer' ? 'CIFER' : 'Shield'}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[600px]">
          <thead>
            <tr>
              {['Date', 'Token', 'Source', 'Tx', 'Amount'].map((h, i) => (
                <th
                  key={h}
                  className={`font-display text-[11px] font-bold uppercase tracking-[0.1em] text-muted text-left px-4 sm:px-8 py-3 border-b border-border-subtle bg-white/[0.015] ${
                    i === 4 ? 'text-right' : ''
                  } ${i === 3 ? 'hidden md:table-cell' : ''}`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map((entry, i) => {
              const cfg = TOKEN_CONFIG[entry.token as keyof typeof TOKEN_CONFIG] || { dot: '#888', label: entry.token, amountClass: 'text-gray-400', suffix: '' };
              const srcCfg = SOURCE_ICONS[entry.source] || SOURCE_ICONS['Daily distribution'];
              const date = new Date(entry.created_at * 1000).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });

              return (
                <tr key={`${page}-${i}`} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 sm:px-8 py-3 sm:py-4 border-b border-border-subtle text-sm text-muted whitespace-nowrap">
                    {date}
                  </td>
                  <td className="px-4 sm:px-8 py-3 sm:py-4 border-b border-border-subtle">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2 h-2 rounded-sm" style={{ background: cfg.dot }} />
                      <span className="font-display text-sm font-semibold">{cfg.label}</span>
                    </span>
                  </td>
                  <td className="px-4 sm:px-8 py-3 sm:py-4 border-b border-border-subtle text-sm text-gray-400">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className={`w-6 h-6 rounded-ternoa flex items-center justify-center ${srcCfg.bg} ${srcCfg.color}`}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                      </span>
                      {entry.source}
                    </span>
                  </td>
                  <td className="px-4 sm:px-8 py-3 sm:py-4 border-b border-border-subtle hidden md:table-cell">
                    {entry.tx_hash ? (
                      <a
                        href={LINKS.explorerTx(entry.tx_hash)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-xs text-muted hover:text-accent transition-colors"
                      >
                        {entry.tx_hash.slice(0, 6)}...{entry.tx_hash.slice(-4)}
                      </a>
                    ) : (
                      <span className="text-xs text-muted/50">off-chain</span>
                    )}
                  </td>
                  <td className="px-4 sm:px-8 py-3 sm:py-4 border-b border-border-subtle text-right">
                    <span className={`font-display text-sm font-bold ${entry.amount < 0 ? 'text-red-400' : cfg.amountClass}`}>
                      {entry.token === 'cifer' ? (
                        <>{entry.amount} {cfg.suffix}</>
                      ) : (
                        <>+{entry.amount.toLocaleString(undefined, { maximumFractionDigits: 1 })} {cfg.suffix}</>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {/* Footer */}
        {filtered.length === 0 && (
          <div className="py-12 text-center text-muted text-sm">No rewards history yet.</div>
        )}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between px-4 sm:px-8 py-4 border-t border-border-subtle">
            <span className="text-xs text-muted">
              Page {page + 1} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className={`font-display text-xs font-semibold px-3 py-1.5 rounded-ternoa border transition-all bg-transparent cursor-pointer font-body ${
                  page === 0
                    ? 'border-border-subtle text-muted/30 cursor-not-allowed'
                    : 'border-border-subtle text-muted hover:text-gray-400 hover:border-border-medium'
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className={`font-display text-xs font-semibold px-3 py-1.5 rounded-ternoa border transition-all bg-transparent cursor-pointer font-body ${
                  page >= totalPages - 1
                    ? 'border-border-subtle text-muted/30 cursor-not-allowed'
                    : 'border-border-subtle text-muted hover:text-gray-400 hover:border-border-medium'
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
